import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Camera, ScanSearch, X, Eye, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { HintMiniMap } from "@/components/HintMiniMap";

// AI-read clues (signs, storefronts, landmarks) + matching pins for one caller photo.
const VisionClues = ({ vision, onPick }) => {
  const [sel, setSel] = useState(null);
  const c = vision.clues || {};
  if (c.error) return <div data-testid="vision-error" className="font-mono text-[9px] text-[#9CA3AF]">AI photo read unavailable for this photo.</div>;
  const bits = [["Signs", c.street_signs], ["Businesses", c.businesses], ["Landmarks", c.landmarks], ["Transit", c.transit], ["House #", c.house_numbers]].filter(([, v]) => v?.length);
  const pick = (cand, i) => { setSel(i); onPick?.(cand); toast.success(`Pinned from photo clue "${cand.clue}" — verify with caller`); };
  return (
    <div data-testid="vision-clues" className="border border-[#7C3AED]/50 bg-[#7C3AED]/10 p-2 space-y-1">
      <div className="font-mono text-[9px] uppercase tracking-wider text-[#C4B5FD] flex items-center gap-1"><ScanSearch size={10} /> Photo vision clues{c.confidence ? ` · ${c.confidence} confidence` : ""}</div>
      {c.summary && <div className="font-mono text-[10px] text-white">{c.summary}</div>}
      {bits.length > 0 && <div className="flex flex-wrap gap-1">{bits.map(([k, v]) => v.map((x, i) => <span key={k + i} className="px-1.5 py-0.5 rounded-sm bg-[#1F2937] font-mono text-[9px] text-[#E9D5FF]"><span className="text-[#A78BFA]">{k}:</span> {x}</span>))}</div>}
      {bits.length === 0 && <div className="font-mono text-[9px] text-[#9CA3AF]">No readable signs or storefronts in this photo.</div>}
      {vision.candidates?.length > 0 && (
        <>
          <HintMiniMap candidates={vision.candidates} selected={sel} onPick={pick} />
          {vision.candidates.map((cand, i) => (
            <button type="button" key={i} data-testid={`vision-candidate-${i}`} onClick={() => pick(cand, i)} disabled={!onPick}
              className={`w-full text-left px-2 py-1 border bg-[#141518] font-mono text-[10px] text-white ${sel === i ? "border-[#A78BFA] bg-[#7C3AED]/20" : "border-[#2C2D35] hover:border-[#A78BFA]"} disabled:cursor-default`}>
              <span className="inline-block w-4 h-4 text-center rounded-full bg-[#4C1D95] text-[#E9D5FF] font-bold mr-1.5 text-[9px] leading-4">{i + 1}</span>
              <span className="text-[#E9D5FF] font-bold">{cand.name || cand.address}</span><span className="text-[#9CA3AF]"> — {cand.address} · from "{cand.clue}"</span>
            </button>
          ))}
        </>
      )}
    </div>
  );
};

// Authenticated thumbnail — caller photos are served through /api/caller-photo (JWT), not public URLs.
const AuthImg = ({ url, className, onClick }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true, obj;
    client.get(url.replace(/^\/api/, ""), { responseType: "blob" }).then((r) => { obj = URL.createObjectURL(r.data); if (alive) setSrc(obj); }).catch(() => {});
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  return src ? <img src={src} alt="caller" className={className} onClick={onClick} /> : <div className={`${className} bg-[#141518] animate-pulse`} />;
};

// Gallery of photos the caller shared from the locate page (+ AI vision clues & suggested pins).
export const CallerPhotos = ({ photos = [], compact = false, onPick, onRepush, pin, title = "Caller photos", showClues = true }) => {
  const [open, setOpen] = useState(null);
  if (!photos.length) return null;
  return (
    <div data-testid="caller-photos" className="space-y-1">
      <div className="font-mono text-[9px] uppercase tracking-wider text-[#93C5FD] flex items-center gap-1"><Camera size={10} /> {title} · {photos.length}
        {onRepush && <button type="button" data-testid="caller-photos-repush" onClick={onRepush} className="ml-auto px-1.5 py-0.5 border border-[#3B82F6]/60 text-[#BFDBFE] hover:bg-[#3B82F6]/20 rounded-sm text-[8px]">PUSH TO UNIT MDTs</button>}
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {photos.map((p, i) => (
          <div key={i} className="relative">
            <AuthImg url={p.url} onClick={() => setOpen(p)} className={`${compact ? "w-14 h-14" : "w-20 h-20"} object-cover rounded-sm border border-[#2C2D35] hover:border-[#3B82F6] cursor-zoom-in`} />
            <div className="absolute bottom-0 inset-x-0 bg-black/70 font-mono text-[7px] text-white text-center">{p.ts.slice(11, 16)}Z</div>
            {p.vision && !p.vision.clues?.error && <div className="absolute -top-1 -right-1 bg-[#7C3AED] rounded-full p-0.5" title="AI clues ready"><ScanSearch size={9} /></div>}
          </div>
        ))}
      </div>
      {showClues && photos.filter((p) => p.vision).map((p, i) => <VisionClues key={i} vision={p.vision} onPick={onPick} />)}
      {showClues && photos.some((p) => !p.vision && Date.now() - new Date(p.ts).getTime() < 180000) && <div className="font-mono text-[9px] text-[#A78BFA] flex items-center gap-1"><ScanSearch size={9} className="animate-pulse" /> Reading signs & storefronts in the photo…</div>}
      {open && (
        <div data-testid="caller-photo-lightbox" className="fixed inset-0 z-[1000] bg-black/90 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="max-w-6xl w-full space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between font-mono text-[11px] text-white">
              <span>CALLER PHOTO · {new Date(open.ts).toLocaleString()}{open.lat ? ` · taken at ${open.lat.toFixed(5)}, ${open.lng.toFixed(5)}` : ""}{open.note ? ` · "${open.note}"` : ""}</span>
              <button onClick={() => setOpen(null)}><X size={16} /></button>
            </div>
            <StreetCompare photo={open} pin={pin} />
          </div>
        </div>
      )}
    </div>
  );
};

// Street View Compare — caller photo beside a street-level view of the pin (or the photo's own GPS) so dispatch can confirm visually.
const StreetCompare = ({ photo, pin }) => {
  const spots = [pin && { label: "CALL PIN", lat: pin.lat, lng: pin.lng, address: pin.address }, photo.lat && { label: "PHOTO GPS", lat: photo.lat, lng: photo.lng }].filter(Boolean);
  const [i, setI] = useState(0);
  const [heading, setHeading] = useState(0);
  const s = spots[Math.min(i, spots.length - 1)];
  const key = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  const gsv = s && `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${s.lat},${s.lng}&heading=${heading}&pitch=0&fov=90`;
  const embed = s && key && `https://www.google.com/maps/embed/v1/streetview?key=${key}&location=${s.lat},${s.lng}&heading=${heading}&pitch=0&fov=90`;
  return (
    <div data-testid="street-compare" className="grid md:grid-cols-2 gap-3">
      <div className="space-y-1">
        <div className="font-mono text-[9px] uppercase tracking-wider text-[#93C5FD]">Caller's photo</div>
        <AuthImg url={photo.url} className="w-full max-h-[62vh] object-contain rounded border border-[#2C2D35] bg-black" />
      </div>
      <div className="space-y-1">
        <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-wider text-[#FCD34D]">
          <Eye size={10} /> Street-level view
          {spots.map((sp, k) => <button key={k} data-testid={`street-spot-${k}`} onClick={() => setI(k)} className={`px-1.5 py-0.5 border rounded-sm ${i === k ? "border-[#FCD34D] bg-[#F59E0B]/20 text-white" : "border-[#2C2D35] text-[#9CA3AF]"}`}>{sp.label}</button>)}
          <span className="ml-auto flex items-center gap-1 normal-case">Face: {["N", "E", "S", "W"].map((d, k) => <button key={d} onClick={() => setHeading(k * 90)} className={`px-1 border rounded-sm ${heading === k * 90 ? "border-[#FCD34D] text-white" : "border-[#2C2D35] text-[#9CA3AF]"}`}>{d}</button>)}</span>
        </div>
        {!s ? <div className="h-48 flex items-center justify-center font-mono text-[10px] text-[#9CA3AF] border border-[#2C2D35] rounded">No pin or photo GPS yet — resolve a clue or pin the call first.</div>
          : embed ? <iframe data-testid="street-embed" title="street view" src={embed} className="w-full h-[52vh] rounded border border-[#2C2D35]" allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          : (
            <div className="rounded border border-[#2C2D35] overflow-hidden">
              <HintMiniMap candidates={[{ lat: s.lat, lng: s.lng, name: s.label, address: s.address || "" }]} selected={0} near={photo.lat && s.label !== "PHOTO GPS" ? { lat: photo.lat, lng: photo.lng } : null} />
              <div className="p-2 font-mono text-[10px] text-[#9CA3AF] space-y-1">
                <div>{s.address || `${s.lat.toFixed(5)}, ${s.lng.toFixed(5)}`}</div>
                <div className="text-[9px]">Street-level imagery opens in Google Street View (no key needed). Add REACT_APP_GOOGLE_MAPS_KEY to embed it here instead.</div>
              </div>
            </div>
          )}
        {s && <a data-testid="street-open" href={gsv} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-head font-bold bg-[#F59E0B] text-black rounded-sm"><ExternalLink size={11} /> OPEN STREET VIEW AT {s.label} ↗</a>}
      </div>
    </div>
  );
};
